import { state } from "../state";
import { continueIntoGame, exportSaveToFile, importSaveFromFile, startNewGame } from "../persistence";
import { formatPlayerName, goToChangeNickname } from "../playerName";
import type { ScreenContext } from "../app";

// Main Menu -- design doc Section 13a (LOCKED — Phase 2 wireframe),
// styled after Voidfront's own menu (public/index.html, shared for
// reference) for overall feel: a dark starfield "attract mode" behind a
// simple vertical button stack, not a static splash screen. Section
// 13a's own animated-background bullets are basically already Voidfront's
// ambient vignettes, just described independently -- "faint background
// traffic," "a distant duel... pulsing shots," "a mining vignette... a
// pulsing extraction beam and drifting ore particles" -- all built here
// in plain CSS/SVG (no canvas loop), purely decorative and disconnected
// from real game state, per the doc's own words.
//
// Real navigation (16.57): Continue calls persistence.ts's
// continueIntoGame(), which resumes directly into whichever top-level
// View a loaded ship (from a local OR server save, see bootAuth's
// local-first check) was last in -- offline and online alike, no network
// round-trip required if a local save exists. New Game is now genuinely
// distinct: if `state.hasExistingSave` (or a ship is already loaded) it
// shows the overwrite-confirmation dialog below before calling
// persistence.ts's startNewGame() (clears the local save + all save-
// backed state, keeps auth/session untouched); with nothing to overwrite,
// it starts fresh immediately, same destination as Continue would reach
// for a brand-new captain.
//
// Export/Import Save (16.44) ARE real now, though, as a distinct feature
// from that gap: a portable local save FILE (persistence.ts's
// exportSaveToFile/importSaveFromFile), working the same whether or not
// the captain is logged in. Export downloads the current live state as
// JSON; Import reads a chosen file back in as the new live state and, if
// authenticated, immediately overwrites the server's save with it too --
// restoring from a file is meant to win outright, not merge.
//
// Login is demoted to a small link/status line at the bottom, per the
// doc's explicit "must never visually compete with Continue/New Game."
// Signed-in state (name + Log Out) is a real, small addition beyond the
// doc's own wireframe, which only showed the logged-out link -- the doc
// flags the signed-in appearance as genuinely unresolved, so this is a
// reasonable filled-in guess, not a locked decision.

export function renderMainMenuScreen(container: HTMLElement, ctx: ScreenContext): void {
  const wrap = document.createElement("div");
  wrap.className = "mm-wrap";

  const checking = state.authStatus === "checking";
  const name = formatPlayerName(state.displayName ?? state.username);
  const authLineHtml =
    state.authStatus === "authenticated"
      ? `<span class="mm-auth-line">Signed in as ${name.text} ` +
        (name.opaque ? `<button type="button" class="mm-btn-changename" data-change-name>Change Name</button> ` : "") +
        `&middot; <button type="button" class="mm-link-btn" data-logout>Log out</button></span>`
      : `<span class="mm-auth-line"><button type="button" class="mm-link-btn" data-login>Log in for cloud sync</button></span>`;

  wrap.innerHTML = `
    <div class="mm-stars" data-stars></div>
    <div class="mm-nebula"></div>
    <div class="mm-scanlines"></div>

    <div class="mm-planet"></div>
    ${stationSvg()}
    ${Array.from({ length: 4 }, (_, i) => driftShipSvg(i)).join("")}
    ${duelVignetteHtml()}
    ${miningVignetteHtml()}

    <!--
      Back to the lab. Starforge is one entry in a larger site rather than a
      standalone game, and without this the menu is a dead end -- the only way
      out is the browser's back button.

      Deliberately not DrsAuth.mountLabBar(), which the static games use: that
      injects a fixed overlay, and this menu is a full-bleed composition with
      its own layers. It gets a link styled to belong here instead. Same
      destination, same position, one less thing floating above the art.
    -->
    <a class="mm-lab-link" href="/" title="Back to Don's AI Game Lab">&laquo; Don's AI Game Lab</a>

    <div class="mm-layout">
      <div class="mm-title-block">
        <div class="mm-eyebrow">Local Director — Sector Command</div>
        <div class="mm-title">STARFORGE</div>
        <div class="mm-subtitle">Command a crewed mining &amp; combat vessel</div>
      </div>

      <div class="mm-menu-buttons">
        <button type="button" class="mm-btn mm-btn-primary" data-continue ${checking ? "disabled" : ""}>
          <span class="mm-btn-icon">&#9654;</span>
          <span class="mm-btn-label">${checking ? "Checking session..." : "Continue"}</span>
        </button>
        <button type="button" class="mm-btn" data-newgame ${checking ? "disabled" : ""}>
          <span class="mm-btn-icon">&#9889;</span>
          <span class="mm-btn-label">New Game</span>
        </button>
        <div class="mm-btn-row">
          <button type="button" class="mm-btn mm-btn-secondary" data-export title="Download your current save as a JSON file">
            <span class="mm-btn-icon">&#128190;</span>
            <span class="mm-btn-label">Export Save</span>
          </button>
          <button type="button" class="mm-btn mm-btn-secondary" data-import title="Load a Starforge save file, replacing your current progress">
            <span class="mm-btn-icon">&#128194;</span>
            <span class="mm-btn-label">Import Save</span>
          </button>
          <input type="file" accept="application/json,.json" data-import-input style="display:none" />
        </div>
        <button type="button" class="mm-btn mm-btn-secondary" data-compare-saves title="See both saves' timestamp and location before choosing which to load">
          <span class="mm-btn-icon">&#128337;</span>
          <span class="mm-btn-label">Compare Saves</span>
        </button>
      </div>

      <div class="mm-auth-row">${authLineHtml}</div>
    </div>

    <div class="mm-footer">Starforge &nbsp;&middot;&nbsp; Local Director</div>

    <div class="mm-confirm-overlay" data-newgame-confirm style="display:none">
      <div class="mm-confirm-dialog">
        <p>Starting a new game will erase your current captain. Are you sure?</p>
        <p class="dim">Use Export Save first if you want a backup.</p>
        <div class="mm-confirm-actions">
          <button type="button" data-newgame-cancel>Cancel</button>
          <button type="button" class="mm-btn-primary" data-newgame-confirm-btn>Erase &amp; Start New Game</button>
        </div>
      </div>
    </div>
  `;

  wrap.querySelector<HTMLButtonElement>("[data-continue]")?.addEventListener("click", () => continueIntoGame(ctx));

  const confirmOverlay = wrap.querySelector<HTMLElement>("[data-newgame-confirm]")!;
  wrap.querySelector<HTMLButtonElement>("[data-newgame]")?.addEventListener("click", () => {
    if (state.currentShip || state.hasExistingSave) {
      confirmOverlay.style.display = "flex";
    } else {
      startNewGame(ctx);
    }
  });
  wrap.querySelector<HTMLButtonElement>("[data-newgame-cancel]")?.addEventListener("click", () => {
    confirmOverlay.style.display = "none";
  });
  wrap.querySelector<HTMLButtonElement>("[data-newgame-confirm-btn]")?.addEventListener("click", () => {
    confirmOverlay.style.display = "none";
    startNewGame(ctx);
  });
  wrap.querySelector<HTMLButtonElement>("[data-login]")?.addEventListener("click", () => ctx.navigate("login"));
  wrap.querySelector<HTMLButtonElement>("[data-change-name]")?.addEventListener("click", goToChangeNickname);
  wrap.querySelector<HTMLButtonElement>("[data-logout]")?.addEventListener("click", async () => {
    const { logoutAndReset } = await import("../persistence");
    await logoutAndReset();
    state.screen = "mainMenu";
    ctx.rerender();
  });

  wrap.querySelector<HTMLButtonElement>("[data-compare-saves]")?.addEventListener("click", () => {
    ctx.navigate("saveCompare");
  });

  wrap.querySelector<HTMLButtonElement>("[data-export]")?.addEventListener("click", () => {
    exportSaveToFile();
  });

  const importInput = wrap.querySelector<HTMLInputElement>("[data-import-input]")!;
  wrap.querySelector<HTMLButtonElement>("[data-import]")?.addEventListener("click", () => {
    importInput.click();
  });
  importInput.addEventListener("change", async () => {
    const file = importInput.files?.[0];
    importInput.value = ""; // allow re-selecting the same file later
    if (!file) return;
    const result = await importSaveFromFile(file, ctx);
    if (result.ok) {
      ctx.rerender();
    } else {
      window.alert(result.error);
    }
  });

  container.appendChild(wrap);

  const starsEl = wrap.querySelector<HTMLElement>("[data-stars]")!;
  for (let i = 0; i < 160; i++) {
    const star = document.createElement("div");
    star.className = "mm-star";
    const size = Math.random() < 0.85 ? Math.random() * 1.5 + 0.5 : Math.random() * 2.5 + 1.5;
    star.style.cssText = `
      left:${Math.random() * 100}%; top:${Math.random() * 100}%;
      width:${size}px; height:${size}px;
      animation-duration:${2 + Math.random() * 5}s;
      animation-delay:${Math.random() * 6}s;
      opacity:${0.1 + Math.random() * 0.5};
    `;
    starsEl.appendChild(star);
  }
}

function stationSvg(): string {
  return `
    <svg class="mm-station" width="130" height="130" viewBox="0 0 130 130" fill="none">
      <circle cx="65" cy="65" r="18" fill="#1a2540" stroke="#3aa0e8" stroke-width="2" />
      <circle cx="65" cy="65" r="46" fill="none" stroke="#2a4a70" stroke-width="3" />
      <circle cx="65" cy="65" r="46" fill="none" stroke="#3aa0e8" stroke-width="1" stroke-dasharray="6 10" opacity="0.6" />
      <rect x="60" y="10" width="10" height="24" fill="#25324a" stroke="#3a5a80" />
      <rect x="60" y="96" width="10" height="24" fill="#25324a" stroke="#3a5a80" />
      <rect x="10" y="60" width="24" height="10" fill="#25324a" stroke="#3a5a80" />
      <rect x="96" y="60" width="24" height="10" fill="#25324a" stroke="#3a5a80" />
      <circle cx="65" cy="65" r="4" fill="#8fd0ff" />
    </svg>
  `;
}

function driftShipSvg(index: number): string {
  const variants = [
    { top: "14%", left: "8%", scale: 1, dur: 22, delay: 0, opacity: 0.5 },
    { top: "62%", left: "78%", scale: 0.7, dur: 30, delay: 3, opacity: 0.32 },
    { top: "80%", left: "20%", scale: 0.55, dur: 26, delay: 6, opacity: 0.24 },
    { top: "30%", left: "60%", scale: 0.85, dur: 34, delay: 2, opacity: 0.4 }
  ];
  const v = variants[index % variants.length];
  return `
    <svg class="mm-drift-ship" style="top:${v.top}; left:${v.left}; opacity:${v.opacity}; --mm-scale:${v.scale}; animation-duration:${v.dur}s; animation-delay:${v.delay}s;"
         width="140" height="70" viewBox="0 0 140 70" fill="none">
      <polygon points="70,0 132,55 108,47 70,58 32,47 8,55" fill="#8ab4ff" />
      <polygon points="70,8 120,51 100,44 70,52 40,44 20,51" fill="#3a6eff" opacity="0.5" />
      <circle cx="70" cy="26" r="4" fill="#aaccff" opacity="0.85" />
    </svg>
  `;
}

function duelVignetteHtml(): string {
  // "A distant duel -- two ships trading pulsing shots" (Section 13a).
  return `
    <div class="mm-vignette mm-duel">
      <svg width="220" height="80" viewBox="0 0 220 80" fill="none">
        <polygon points="20,40 45,30 45,50" fill="#ff6a5a" opacity="0.75" />
        <polygon points="200,40 175,30 175,50" fill="#5ab4ff" opacity="0.75" />
        <line x1="48" y1="40" x2="172" y2="40" class="mm-laser mm-laser-a" />
        <line x1="172" y1="40" x2="48" y2="40" class="mm-laser mm-laser-b" />
        <circle cx="45" cy="40" r="5" class="mm-muzzle mm-muzzle-a" />
        <circle cx="175" cy="40" r="5" class="mm-muzzle mm-muzzle-b" />
      </svg>
    </div>
  `;
}

function miningVignetteHtml(): string {
  // "A mining vignette -- a ship working an asteroid with a pulsing
  // extraction beam and drifting ore particles" (Section 13a).
  return `
    <div class="mm-vignette mm-mining">
      <svg width="180" height="90" viewBox="0 0 180 90" fill="none">
        <polygon points="20,45 46,35 46,55" fill="#8ab4ff" opacity="0.8" />
        <circle cx="140" cy="45" r="22" fill="#5a4a3a" opacity="0.85" />
        <circle cx="132" cy="38" r="5" fill="#463a2c" opacity="0.7" />
        <circle cx="148" cy="52" r="4" fill="#463a2c" opacity="0.7" />
        <line x1="48" y1="45" x2="120" y2="45" class="mm-mining-beam" />
        <circle class="mm-ore mm-ore-1" cx="120" cy="45" r="2.4" fill="#e0c080" />
        <circle class="mm-ore mm-ore-2" cx="120" cy="45" r="2" fill="#e0c080" />
        <circle class="mm-ore mm-ore-3" cx="120" cy="45" r="2.6" fill="#e0c080" />
      </svg>
    </div>
  `;
}
